'use client';

import { useRef, useState } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';

import {
  AnimatePresence,
  motion,
  useScroll,
  useSpring,
  useTransform,
  type Variants,
} from 'framer-motion';
import { ArrowUpRight, Check, Plus } from 'lucide-react';

import type { LandingPageProps } from '../_lib/landing-page.types';

import './styles.css';

/* ---------------------------------------------------------------- *
 * Content
 * ---------------------------------------------------------------- */

const FACILITIES = [
  {
    n: '01',
    title: 'Beach & Pool',
    sub: 'Sun, salt, stillness.',
    src: '/landing-pages/oia/images/facility-beach-pool.webp',
  },
  {
    n: '02',
    title: 'Eat & Drink',
    sub: 'Considered seasonal menus.',
    src: '/landing-pages/oia/images/facility-eat-drink.webp',
  },
  {
    n: '03',
    title: 'Gym & Wellness',
    sub: 'Movement as a daily ritual.',
    src: '/landing-pages/oia/images/facility-gym-wellness.webp',
  },
  {
    n: '04',
    title: 'Beauty & Spa',
    sub: 'Treatments shaped by ritual.',
    src: '/landing-pages/oia/images/facility-beauty-spa.webp',
  },
  {
    n: '05',
    title: 'Co-Working',
    sub: 'A quiet place to think.',
    src: '/landing-pages/oia/images/facility-coworking.webp',
  },
  {
    n: '06',
    title: 'Boutique',
    sub: 'Curated objects to bring home.',
    src: '/landing-pages/oia/images/facility-boutique.webp',
  },
  {
    n: '07',
    title: 'The Sea Room',
    sub: 'For gathering, slowly.',
    src: '/landing-pages/oia/images/facility-the-sea-room.webp',
  },
];

type TierBenefit = { label: string; items: string[] };

type Tier = {
  n: string;
  name: string;
  price: string;
  unit: string;
  tagline: string;
  src: string;
  benefits: TierBenefit[];
};

const TIERS: Tier[] = [
  {
    n: '01',
    name: 'NYX',
    price: '13,500',
    unit: 'SAR / year',
    tagline:
      'An introduction to OIA. Workspace, wellness, and the quiet rituals of the club.',
    src: '/landing-pages/oia/images/tier-1.webp',
    benefits: [
      {
        label: 'Guest Access',
        items: ['1 guest per month'],
      },
      {
        label: 'Access',
        items: ['Lockers', 'Changing Room', 'Valet Parking'],
      },
      {
        label: 'Beauty & Spa',
        items: ['10% off nail services', '10% off massage', '10% off blow-dry'],
      },
      {
        label: 'Fitness & Wellness',
        items: ['Initial assessment', '10% off classes', '10% off yoga'],
      },
      {
        label: 'Co-Working',
        items: ['24 hours / month'],
      },
      {
        label: 'The Sea Room',
        items: ['10% VIP lounge discount'],
      },
    ],
  },
  {
    n: '02',
    name: 'NYX+',
    price: '17,500',
    unit: 'SAR / year',
    tagline:
      'A quiet rhythm of movement, treatments and uninterrupted focus.',
    src: '/landing-pages/oia/images/tier-2.webp',
    benefits: [
      {
        label: 'Guest Access',
        items: ['1 guest per day'],
      },
      {
        label: 'Access',
        items: ['Lockers', 'Changing Room', 'Valet Parking'],
      },
      {
        label: 'Beauty & Spa',
        items: ['10% off across all services'],
      },
      {
        label: 'Fitness & Wellness',
        items: [
          'Initial assessment',
          '10% off personal training',
          '10% off classes',
          '10% off yoga',
        ],
      },
      {
        label: 'Co-Working',
        items: ['Unlimited access', 'Quiet pods included'],
      },
      {
        label: 'The Sea Room',
        items: ['10% VIP lounge discount'],
      },
    ],
  },
  {
    n: '03',
    name: 'EOS',
    price: '30,000',
    unit: 'SAR / year',
    tagline:
      'Beach mornings, curated wellness, and a place to gather.',
    src: '/landing-pages/oia/images/tier-3.webp',
    benefits: [
      {
        label: 'Guest Access',
        items: ['1 guest per month'],
      },
      {
        label: 'Access',
        items: ['Beach', 'Pool', 'Lockers', 'Changing Room', 'Valet Parking'],
      },
      {
        label: 'Beauty & Spa',
        items: [
          '2 free nail services + 10%',
          '2 free massages annually',
          '10% off blow-dry',
        ],
      },
      {
        label: 'Fitness & Wellness',
        items: [
          'Initial assessment',
          '6 free classes + 10%',
          '4 free yoga + 10%',
        ],
      },
      {
        label: 'Co-Working',
        items: ['24 hours / month', 'Quiet pods included', '10% discount'],
      },
      {
        label: 'The Sea Room',
        items: ['10% VIP lounge discount'],
      },
    ],
  },
  {
    n: '04',
    name: 'IRIS',
    price: '36,500',
    unit: 'SAR / year',
    tagline:
      'A fuller life at the club — daily guests, beach access, and treatments to look forward to.',
    src: '/landing-pages/oia/images/tier-4.webp',
    benefits: [
      {
        label: 'Guest Access',
        items: ['1 guest per day'],
      },
      {
        label: 'Access',
        items: [
          'Beach',
          'Pool',
          'Lockers',
          'Changing Room',
          'Valet Parking',
          '1 monthly OIA Beach experience',
        ],
      },
      {
        label: 'Beauty & Spa',
        items: [
          '4 free nail services + 10%',
          '4 free massages',
          '15% off blow-dry',
        ],
      },
      {
        label: 'Fitness & Wellness',
        items: [
          'Initial assessment',
          '16 free classes + 20%',
          '6 free yoga + 10%',
          '10% off personal training',
        ],
      },
      {
        label: 'Co-Working',
        items: ['36 hours / month', 'Quiet pods included', '20% discount'],
      },
      {
        label: 'The Sea Room',
        items: ['1 free VIP lounge', '10% discount thereafter'],
      },
    ],
  },
  {
    n: '05',
    name: 'TARA',
    price: '36,500',
    unit: 'SAR / year',
    tagline:
      'For deeper work hours and an unhurried daily ritual at the club.',
    src: '/landing-pages/oia/images/tier-5.webp',
    benefits: [
      {
        label: 'Guest Access',
        items: ['1 guest per day'],
      },
      {
        label: 'Access',
        items: [
          'Beach',
          'Pool',
          'Lockers',
          'Changing Room',
          'Valet Parking',
          '1 monthly OIA Beach experience',
        ],
      },
      {
        label: 'Beauty & Spa',
        items: [
          '4 free nail services + 10%',
          '4 free massages',
          '15% off blow-dry',
        ],
      },
      {
        label: 'Fitness & Wellness',
        items: [
          'Initial assessment',
          '8 free classes + 20%',
          '6 free yoga + 10%',
        ],
      },
      {
        label: 'Co-Working',
        items: ['72 hours / month', 'Quiet pods included', '20% discount'],
      },
      {
        label: 'The Sea Room',
        items: ['1 free VIP lounge', '10% discount thereafter'],
      },
    ],
  },
  {
    n: '06',
    name: 'ATHENA',
    price: '72,000',
    unit: 'SAR / year',
    tagline:
      'The unabridged OIA. Unlimited time, deepest care, three daily guests.',
    src: '/landing-pages/oia/images/tier-6.webp',
    benefits: [
      {
        label: 'Guest Access',
        items: ['3 guests per day'],
      },
      {
        label: 'Access',
        items: ['Beach', 'Pool', 'Lockers', 'Changing Room', 'Valet Parking'],
      },
      {
        label: 'Beauty & Spa',
        items: [
          '24 free nail services + 10%',
          '12 free massages + 10%',
          '12 free blow-dry + 10%',
        ],
      },
      {
        label: 'Fitness & Wellness',
        items: [
          'Initial assessment',
          '12 free personal training + 10%',
          '12 free classes + 10%',
          '12 free yoga + 10%',
        ],
      },
      {
        label: 'Co-Working',
        items: ['Unlimited access', 'Quiet pods included'],
      },
      {
        label: 'The Sea Room',
        items: ['2 free VIP lounge', '10% discount thereafter'],
      },
    ],
  },
];

const FAQS = [
  {
    q: 'What is OIA Downtown?',
    a: 'OIA Downtown is an exclusive women-only membership club in the heart of Jeddah, designed as a sanctuary where members rejuvenate, connect, and thrive — with curated amenities across beach, dining, wellness, and culture.',
  },
  {
    q: 'Who can become a member?',
    a: 'Membership is by invitation only and reviewed on application. The community is composed of women who value refinement, intentional living, and meaningful connection.',
  },
  {
    q: 'How do I become a member?',
    a: 'Begin with our online membership application. Our team reviews each application personally and follows up within a few business days to invite you into the next step.',
  },
  {
    q: 'What services and spaces are available at the club?',
    a: 'Beach & pool, fine dining, gym & wellness programming, beauty & spa, a co-working space, a curated boutique, private lounges, and The Sea Room — alongside cultural collaborations and events.',
  },
  {
    q: 'Can I bring guests to the club?',
    a: 'Yes — guest access is available under specific circumstances for events and special arrangements. Premium tiers include daily guest passes.',
  },
  {
    q: 'What are the operating hours of the club?',
    a: 'OIA Downtown is open daily from 8:00 AM to 5:00 PM, with extended hours for events and member experiences.',
  },
];

const MARQUEE_WORDS = [
  'Slow living',
  'Serene spaces',
  'Refined sophistication',
  'Meaningful connection',
  'Art of balance',
  'Coastal Jeddah',
];

/* ---------------------------------------------------------------- *
 * Motion presets
 * ---------------------------------------------------------------- */

const easeOut = [0.22, 1, 0.36, 1] as const;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 36 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: easeOut },
  },
};

const stagger: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

/* ---------------------------------------------------------------- *
 * Top-level page
 * ---------------------------------------------------------------- */

export default function OiaLandingPage(_props: LandingPageProps) {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.4,
  });

  return (
    <main className="bg-[var(--oia-cream)] text-[var(--oia-ink)]">
      <motion.div className="oia-progress" style={{ scaleX }} />

      <Hero />
      <MarqueeBand />
      <OurStory />
      <Membership />
      <Memberships />
      <Facilities />
      <Faqs />
    </main>
  );
}

/* ---------------------------------------------------------------- *
 * Hero — parallax image + cursor accent + animated letters
 * ---------------------------------------------------------------- */

function Hero() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const yImage = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const yContent = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-[var(--oia-ink)]"
    >
      {/* Parallax image */}
      <motion.div className="absolute inset-0" style={{ y: yImage }}>
        <motion.div
          initial={{ scale: 1.18, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: easeOut }}
          className="absolute inset-0"
        >
          <Image
            src="/landing-pages/oia/images/hero-architecture.webp"
            alt="OIA Downtown — Jeddah"
            fill
            priority
            sizes="100vw"
            className="object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/15 to-black/65" />
        <div className="oia-grain oia-grain-dark" />
      </motion.div>

      {/* Bottom: lead copy + CTAs */}
      <motion.div
        className="absolute inset-x-0 bottom-12 lg:bottom-20"
        style={{ y: yContent, opacity }}
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          {/* Animated thin hairline before the title */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.4, ease: easeOut, delay: 0.4 }}
            className="mb-8 hidden h-px w-32 origin-left bg-white/50 lg:block"
          />

          <motion.div
            initial="hidden"
            animate="show"
            variants={stagger}
            className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
          >
            <h1 className="sr-only">
              OIA Downtown — a sanctuary where women rejuvenate, connect, and thrive.
            </h1>

            <motion.p
              variants={fadeUp}
              className="font-oia-italic max-w-md text-base text-white/85 lg:text-lg"
            >
              A place where women can find balance and embrace the art of slow
              living, surrounded by serene spaces and meaningful connections.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:flex-shrink-0"
            >
              <Link
                href="/membership"
                className="oia-btn-shimmer font-oia-eyebrow group inline-flex h-12 items-center justify-center gap-3 rounded-full bg-[var(--oia-cream)] px-7 text-[11px] text-[var(--oia-ink)] transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>APPLY FOR MEMBERSHIP</span>
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
              <Link
                href="#our-story"
                className="font-oia-eyebrow group inline-flex h-12 items-center justify-center gap-3 rounded-full border border-white/60 px-7 text-[11px] text-white transition-colors duration-300 hover:bg-white/10"
              >
                DISCOVER MORE
                <span className="block h-px w-6 origin-left bg-white transition-transform duration-300 group-hover:scale-x-150" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

    </section>
  );
}

/* ---------------------------------------------------------------- *
 * Marquee band — slow, editorial words drifting
 * ---------------------------------------------------------------- */

function MarqueeBand() {
  const items = [...MARQUEE_WORDS, ...MARQUEE_WORDS];
  return (
    <div className="relative overflow-hidden border-y border-[var(--oia-line)] bg-[var(--oia-cream)] py-6 lg:py-8">
      <div className="oia-marquee-track flex w-[200%] gap-12 whitespace-nowrap will-change-transform">
        {items.map((w, i) => (
          <span
            key={i}
            className="font-oia-italic inline-flex items-center gap-12 text-2xl text-[var(--oia-ink)] sm:text-3xl lg:text-4xl"
          >
            {w}
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--oia-gold)]" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Our Story — sticky split with parallax image
 * ---------------------------------------------------------------- */

function OurStory() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section
      id="our-story"
      className="relative bg-[var(--oia-cream)] py-24 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        {/* Chapter heading */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-15% 0px' }}
          variants={stagger}
          className="mb-16 flex items-baseline justify-between gap-6 lg:mb-24"
        >
          <motion.span
            variants={fadeUp}
            className="font-oia-chapter text-2xl text-[var(--oia-muted)] lg:text-3xl"
          >
            I.
          </motion.span>
          <motion.span
            variants={fadeUp}
            className="font-oia-eyebrow text-[10px] text-[var(--oia-muted)] lg:text-[11px]"
          >
            / OUR STORY
          </motion.span>
        </motion.div>

        {/* Big quote heading */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ duration: 1.1, ease: easeOut }}
          className="font-oia-display mx-auto max-w-5xl text-center text-[clamp(2.5rem,6vw,5.5rem)] leading-[1.04] tracking-[0.02em]"
        >
          <span className="font-oia-italic text-[var(--oia-gold)]">“</span>
          Pursuit of Balance
          <span className="font-oia-italic text-[var(--oia-gold)]">”</span>
        </motion.h2>

        <div
          ref={ref}
          className="mt-20 grid items-center gap-12 lg:mt-32 lg:grid-cols-12 lg:gap-20"
        >
          {/* Image */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ clipPath: 'inset(100% 0 0 0)' }}
              whileInView={{ clipPath: 'inset(0% 0 0 0)' }}
              viewport={{ once: true, margin: '-15% 0px' }}
              transition={{ duration: 1.6, ease: easeOut }}
              className="relative aspect-[3/4] w-full overflow-hidden bg-[var(--oia-sand)] lg:aspect-[4/5]"
            >
              <motion.div className="absolute inset-0" style={{ y: yImg }}>
                <Image
                  src="/landing-pages/oia/images/our-story.webp"
                  alt="Hand reaching into water — slow living"
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover"
                />
              </motion.div>
              <div className="oia-grain" />
            </motion.div>

            {/* Caption */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 1 }}
              className="font-oia-italic mt-4 text-sm text-[var(--oia-muted)]"
            >
              <span className="font-oia-eyebrow mr-2 text-[10px] tracking-[0.25em]">
                FIG. 01
              </span>
              The art of slow living.
            </motion.p>
          </div>

          {/* Copy */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-15% 0px' }}
            variants={stagger}
            className="lg:col-span-6 lg:pl-8"
          >
            <motion.p
              variants={fadeUp}
              className="font-oia-display text-[clamp(1.5rem,2.4vw,2.25rem)] leading-[1.4] text-[var(--oia-ink)]"
            >
              A place where women can find balance and embrace the art of slow
              living, surrounded by{' '}
              <span className="font-oia-italic text-[var(--oia-gold)]">
                serene spaces
              </span>{' '}
              and meaningful connections in thoughtfully chosen locations,
              crafted for a truly personal experience.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-12 flex items-center gap-4">
              <Link
                href="/membership"
                className="font-oia-eyebrow group inline-flex items-center gap-4 text-[11px] text-[var(--oia-ink)] transition-opacity hover:opacity-60"
              >
                <span className="block h-px w-10 origin-left bg-[var(--oia-ink)] transition-transform duration-500 group-hover:scale-x-150" />
                READ MORE
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- *
 * Membership — full-bleed sunset with parallax & masked title
 * ---------------------------------------------------------------- */

function Membership() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);

  return (
    <section
      ref={ref}
      id="membership"
      className="relative overflow-hidden bg-[var(--oia-ink)]"
    >
      <div className="relative h-[100svh] min-h-[620px] w-full">
        <motion.div className="absolute inset-0" style={{ y, scale }}>
          <Image
            src="/landing-pages/oia/images/membership-sunset.webp"
            alt="Sunset over the sea — step into a serene world"
            fill
            sizes="100vw"
            className="object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />

        <div className="relative z-10 flex h-full items-center justify-center px-6 text-center lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-20% 0px' }}
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div
              variants={fadeUp}
              className="flex items-center justify-center gap-4"
            >
              <span className="block h-px w-10 bg-white/60" />
              <span className="font-oia-eyebrow text-[10px] text-white/85">
                II. / MEMBERSHIP
              </span>
              <span className="block h-px w-10 bg-white/60" />
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-oia-display mt-8 text-[clamp(2.75rem,6.5vw,6rem)] leading-[1.04] text-white"
            >
              Step into a
              <br />
              <span className="font-oia-italic text-[var(--oia-gold-soft)]">
                serene world
              </span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="font-oia-body mx-auto mt-10 max-w-xl text-sm leading-relaxed text-white/85 lg:text-base"
            >
              Where your journey to connection, balance, and beauty begins.
              Joining us means stepping into a thoughtfully crafted, exclusive
              community where intimacy and elegance converge.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-12">
              <Link
                href="/membership"
                className="oia-btn-shimmer font-oia-eyebrow group inline-flex h-12 items-center gap-4 rounded-full border border-white/70 bg-white/5 px-8 text-[11px] text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/15"
              >
                <span>EXPLORE MEMBERSHIPS</span>
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Corner data labels */}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- *
 * Memberships — six-tier editorial list with expanding benefits
 * ---------------------------------------------------------------- */

function Memberships() {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  return (
    <section
      id="memberships"
      className="relative bg-[var(--oia-cream)] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        {/* Chapter header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-15% 0px' }}
          variants={stagger}
          className="mb-14 flex items-baseline justify-between gap-6 lg:mb-20"
        >
          <motion.span
            variants={fadeUp}
            className="font-oia-chapter text-2xl text-[var(--oia-muted)] lg:text-3xl"
          >
            III.
          </motion.span>
          <motion.span
            variants={fadeUp}
            className="font-oia-eyebrow text-[10px] text-[var(--oia-muted)] lg:text-[11px]"
          >
            / MEMBERSHIPS
          </motion.span>
        </motion.div>

        {/* Section heading */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ duration: 1.1, ease: easeOut }}
          className="font-oia-display max-w-3xl text-[clamp(2.5rem,5.5vw,5rem)] leading-[1.04] tracking-[0.02em]"
        >
          A membership for{' '}
          <span className="font-oia-italic text-[var(--oia-gold)]">
            every rhythm
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 1 }}
          className="font-oia-italic mt-6 max-w-xl text-base text-[var(--oia-muted)]"
        >
          From a quiet workspace to the full sweep of the club — six tiers, each
          designed around a particular way of spending time at OIA.
        </motion.p>
      </div>

      {/* Sticky image / scrolling list */}
      <div className="mx-auto mt-16 max-w-[1400px] px-6 lg:mt-24 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Sticky image column (desktop only) */}
          <div className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-[var(--oia-sand)]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={TIERS[activeIndex]?.src}
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.9, ease: easeOut }}
                    className="absolute inset-0"
                  >
                    <Image
                      src={TIERS[activeIndex]?.src ?? TIERS[0]!.src}
                      alt={TIERS[activeIndex]?.name ?? 'Membership'}
                      fill
                      sizes="(max-width: 1024px) 100vw, 40vw"
                      className="object-cover"
                    />
                  </motion.div>
                </AnimatePresence>
                <div className="oia-grain" />

                {/* Floating data label */}
                <div className="absolute right-5 bottom-5 left-5 flex items-end justify-between text-white">
                  <div>
                    <p className="font-oia-chapter text-3xl">
                      {TIERS[activeIndex]?.n}
                    </p>
                    <p className="font-oia-display mt-1 text-2xl">
                      {TIERS[activeIndex]?.name}
                    </p>
                  </div>
                  <p className="font-oia-italic text-sm text-white/85">
                    {TIERS[activeIndex]?.unit}
                  </p>
                </div>
              </div>

              {/* Tier index */}
              <div className="mt-6 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {TIERS.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`View tier ${i + 1}`}
                      onClick={() => setActiveIndex(i)}
                      className={`h-1 transition-all duration-500 ${
                        i === activeIndex
                          ? 'w-10 bg-[var(--oia-ink)]'
                          : 'w-5 bg-[var(--oia-line)]'
                      }`}
                    />
                  ))}
                </div>
                <span className="font-oia-eyebrow text-[10px] text-[var(--oia-muted)]">
                  {String(activeIndex + 1).padStart(2, '0')} /{' '}
                  {String(TIERS.length).padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>

          {/* Tier rows */}
          <div className="lg:col-span-7">
            <ul className="border-t border-[var(--oia-line)]">
              {TIERS.map((tier, index) => (
                <TierRow
                  key={tier.name}
                  tier={tier}
                  index={index}
                  isActive={index === activeIndex}
                  onActivate={() => setActiveIndex(index)}
                />
              ))}
            </ul>

            {/* Bottom CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 0.9, ease: easeOut }}
              className="mt-14 flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between"
            >
              <p className="font-oia-italic max-w-md text-base text-[var(--oia-muted)]">
                Memberships go beyond perks — they’re about finding your people
                and creating meaningful connections.
              </p>
              <Link
                href="/membership"
                className="oia-btn-shimmer font-oia-eyebrow group inline-flex h-12 items-center gap-3 rounded-full bg-[var(--oia-ink)] px-7 text-[11px] text-[var(--oia-cream)] transition-transform duration-300 hover:scale-[1.02]"
              >
                APPLY FOR MEMBERSHIP
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TierRow({
  tier,
  index,
  isActive,
  onActivate,
}: {
  tier: Tier;
  index: number;
  isActive: boolean;
  onActivate: () => void;
}) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{ duration: 0.8, ease: easeOut, delay: index * 0.04 }}
      onMouseEnter={onActivate}
      className="border-b border-[var(--oia-line)]"
    >
      <button
        type="button"
        onClick={onActivate}
        className="group flex w-full items-baseline justify-between gap-4 py-7 text-left transition-colors lg:gap-8"
        aria-expanded={isActive}
      >
        <div className="flex items-baseline gap-5 lg:gap-7">
          <span className="font-oia-chapter text-sm text-[var(--oia-muted)]">
            {tier.n}
          </span>
          <span
            className={`font-oia-display text-2xl leading-tight transition-colors sm:text-3xl lg:text-4xl ${
              isActive
                ? 'text-[var(--oia-ink)]'
                : 'text-[var(--oia-ink)]/55 group-hover:text-[var(--oia-ink)]'
            }`}
          >
            {tier.name}
          </span>
        </div>
        <div className="text-right">
          <p
            className={`font-oia-display text-xl transition-colors sm:text-2xl lg:text-3xl ${
              isActive
                ? 'text-[var(--oia-ink)]'
                : 'text-[var(--oia-ink)]/55 group-hover:text-[var(--oia-ink)]'
            }`}
          >
            {tier.price}
          </p>
          <p className="font-oia-eyebrow text-[9px] text-[var(--oia-muted)] lg:text-[10px]">
            {tier.unit}
          </p>
        </div>
      </button>

      {/* Mobile-only inline image when active */}
      <AnimatePresence initial={false}>
        {isActive && (
          <motion.div
            key="mobile-image"
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: 'auto',
              opacity: 1,
              transition: {
                height: { duration: 0.5, ease: easeOut },
                opacity: { duration: 0.4, delay: 0.1 },
              },
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                height: { duration: 0.4, ease: easeOut },
                opacity: { duration: 0.2 },
              },
            }}
            className="overflow-hidden lg:hidden"
          >
            <div className="relative mb-6 aspect-[5/4] w-full overflow-hidden bg-[var(--oia-sand)]">
              <Image
                src={tier.src}
                alt={tier.name}
                fill
                sizes="100vw"
                className="object-cover"
              />
              <div className="oia-grain" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded benefits */}
      <AnimatePresence initial={false}>
        {isActive && (
          <motion.div
            key="benefits"
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: 'auto',
              opacity: 1,
              transition: {
                height: { duration: 0.55, ease: easeOut },
                opacity: { duration: 0.4, delay: 0.1 },
              },
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                height: { duration: 0.4, ease: easeOut },
                opacity: { duration: 0.2 },
              },
            }}
            className="overflow-hidden"
          >
            <div className="pb-8 lg:pl-12">
              <p className="font-oia-italic mb-8 max-w-xl text-base text-[var(--oia-muted)] lg:text-lg">
                {tier.tagline}
              </p>

              <div className="grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2">
                {tier.benefits.map((group) => (
                  <div key={group.label}>
                    <p className="font-oia-eyebrow text-[9px] tracking-[0.25em] text-[var(--oia-gold)] lg:text-[10px]">
                      {group.label}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {group.items.map((item) => (
                        <li
                          key={item}
                          className="font-oia-body flex items-start gap-2.5 text-sm text-[var(--oia-ink)]/80"
                        >
                          <Check className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-[var(--oia-gold)]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
}

/* ---------------------------------------------------------------- *
 * Facilities — horizontal scroll with editorial cards
 * ---------------------------------------------------------------- */

function Facilities() {
  const trackRef = useRef<HTMLDivElement>(null);

  return (
    <section
      id="facilities"
      className="relative bg-[var(--oia-cream-soft)] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-15% 0px' }}
          variants={stagger}
          className="mb-14 flex items-baseline justify-between gap-6 lg:mb-20"
        >
          <motion.span
            variants={fadeUp}
            className="font-oia-chapter text-2xl text-[var(--oia-muted)] lg:text-3xl"
          >
            IV.
          </motion.span>
          <motion.span
            variants={fadeUp}
            className="font-oia-eyebrow text-[10px] text-[var(--oia-muted)] lg:text-[11px]"
          >
            / FACILITIES
          </motion.span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ duration: 1.1, ease: easeOut }}
          className="font-oia-display max-w-3xl text-[clamp(2.5rem,5vw,4.5rem)] leading-[1.05] tracking-[0.02em]"
        >
          Thoughtfully{' '}
          <span className="font-oia-italic text-[var(--oia-gold)]">
            curated
          </span>{' '}
          to slow down and savor.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 1 }}
          className="font-oia-italic mt-6 max-w-xl text-base text-[var(--oia-muted)]"
        >
          From sunlit pool mornings to beauty rituals and quiet workspace —
          every space is a chapter of the day.
        </motion.p>
      </div>

      {/* Horizontally scrolling facility cards */}
      <div className="relative mt-16 lg:mt-24">
        <div
          ref={trackRef}
          className="oia-scroll-x flex gap-6 overflow-x-auto px-6 pb-6 lg:gap-10 lg:px-12"
        >
          {FACILITIES.map((facility, i) => (
            <motion.article
              key={facility.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{
                duration: 0.9,
                ease: easeOut,
                delay: Math.min(i * 0.07, 0.5),
              }}
              className="w-[260px] flex-shrink-0 snap-start sm:w-[320px] lg:w-[380px]"
            >
              <div className="oia-card relative aspect-[3/4] w-full bg-[var(--oia-sand)]">
                <Image
                  src={facility.src}
                  alt={facility.title}
                  fill
                  sizes="(max-width: 640px) 80vw, (max-width: 1024px) 50vw, 380px"
                  className="object-cover"
                />
                <div className="absolute top-4 left-4 z-10 flex items-center gap-2 text-white">
                  <span className="font-oia-chapter text-base">
                    {facility.n}
                  </span>
                  <span className="block h-px w-6 bg-white/70" />
                </div>
                <div className="absolute right-4 bottom-4 left-4 z-10 text-white">
                  <p className="font-oia-display text-2xl leading-tight lg:text-3xl">
                    {facility.title}
                  </p>
                  <p className="font-oia-italic mt-2 text-sm text-white/85">
                    {facility.sub}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
          {/* End spacer */}
          <div className="w-6 flex-shrink-0 lg:w-12" aria-hidden />
        </div>

        {/* Edge fade hints */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[var(--oia-cream-soft)] to-transparent lg:w-24" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[var(--oia-cream-soft)] to-transparent lg:w-24" />
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3, duration: 1 }}
        className="font-oia-eyebrow mx-auto mt-10 max-w-[1400px] px-6 text-center text-[10px] tracking-[0.3em] text-[var(--oia-muted)] lg:px-12"
      >
        ← DRAG OR SCROLL TO EXPLORE →
      </motion.p>
    </section>
  );
}

/* ---------------------------------------------------------------- *
 * FAQs — animated accordion
 * ---------------------------------------------------------------- */

function Faqs() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faqs" className="bg-[var(--oia-cream)] py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-15% 0px' }}
          variants={stagger}
          className="mb-16 flex items-baseline justify-between gap-6 lg:mb-24"
        >
          <motion.span
            variants={fadeUp}
            className="font-oia-chapter text-2xl text-[var(--oia-muted)] lg:text-3xl"
          >
            V.
          </motion.span>
          <motion.span
            variants={fadeUp}
            className="font-oia-eyebrow text-[10px] text-[var(--oia-muted)] lg:text-[11px]"
          >
            / FREQUENTLY ASKED
          </motion.span>
        </motion.div>

        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-4">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 1.1, ease: easeOut }}
              className="font-oia-display text-[clamp(3rem,5.5vw,5rem)] leading-[1.02] tracking-[0.02em]"
            >
              Q & A.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="font-oia-italic mt-6 max-w-sm text-base text-[var(--oia-muted)]"
            >
              Quietly answered. Reach out anytime at{' '}
              <a
                href="mailto:hello@oiadowntown.com"
                className="text-[var(--oia-ink)] underline-offset-4 hover:underline"
              >
                hello@oiadowntown.com
              </a>
              .
            </motion.p>
          </div>

          <div className="lg:col-span-8">
            <ul>
              {FAQS.map((item, index) => {
                const isOpen = openIndex === index;
                return (
                  <motion.li
                    key={item.q}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-5% 0px' }}
                    transition={{
                      duration: 0.7,
                      ease: easeOut,
                      delay: index * 0.05,
                    }}
                    className="border-t border-[var(--oia-line)] last:border-b"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className="group flex w-full items-center justify-between gap-6 py-7 text-left"
                      aria-expanded={isOpen}
                    >
                      <span className="flex items-baseline gap-5">
                        <span className="font-oia-chapter text-sm text-[var(--oia-muted)]">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="font-oia-display text-lg leading-snug text-[var(--oia-ink)] transition-colors group-hover:text-[var(--oia-gold)] sm:text-xl lg:text-2xl">
                          {item.q}
                        </span>
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.45, ease: easeOut }}
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-[var(--oia-line)] text-[var(--oia-ink)]"
                      >
                        <Plus className="h-4 w-4" />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{
                            height: 'auto',
                            opacity: 1,
                            transition: {
                              height: { duration: 0.5, ease: easeOut },
                              opacity: { duration: 0.4, delay: 0.1 },
                            },
                          }}
                          exit={{
                            height: 0,
                            opacity: 0,
                            transition: {
                              height: { duration: 0.4, ease: easeOut },
                              opacity: { duration: 0.2 },
                            },
                          }}
                          className="overflow-hidden"
                        >
                          <p className="font-oia-body mr-12 mb-7 max-w-2xl pl-12 text-sm leading-relaxed text-[var(--oia-muted)] sm:text-base">
                            {item.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

